import React, { useCallback, useEffect } from "react";
import L, { LeafletMouseEvent } from "leaflet";
import wkx from "wkx";
import * as TurfHelpers from "@turf/helpers";
import "leaflet-lasso";
import { Feature, Geometry } from "geojson";

import { Segment } from "src/types";

interface Props {
  map: L.Map;
  segments: Segment[];
  selected: Segment["segmentId"][];
  onSelect: (segment: Segment, event: L.LeafletMouseEvent) => void;
}

const segmentStyle: { [key: string]: L.PathOptions | L.StyleFunction<any> } = {
  default: { color: "#40abff", weight: 4 },
  hover: {
    color: "tomato",
    weight: 6,
    lineCap: "butt",
    opacity: 0.6,
  },
  selected: { color: "tomato", weight: 4 },
};

export const SegmentsOnMap = React.memo(function SegmentGeoJSON(props: Props) {
  const map = props.map;

  const handleEachFeature = useCallback(
    (feature: Feature<Geometry, any>, layer: L.GeoJSON) => {
      const segmentId = feature.properties.id;

      // set selected style
      const isSelected = props.selected.includes(segmentId);
      if (isSelected) layer.setStyle(segmentStyle.selected);

      // handle click event
      layer.on("click", (event: LeafletMouseEvent) => {
        // find segment
        const segment = props.segments.find((s) => s.segmentId === segmentId);
        // trigger onSelect callback
        if (segment) props.onSelect(segment, event);
      });

      layer.on("mouseover", (event: LeafletMouseEvent) => {
        if (isSelected) layer.setStyle({ ...segmentStyle.selected });
        else layer.setStyle(segmentStyle.hover);
      });
      layer.on("mouseout", (event: LeafletMouseEvent) => {
        if (isSelected) layer.setStyle(segmentStyle.selected);
        else layer.setStyle(segmentStyle.default);
      });
    },
    [props]
  );

  useEffect(() => {
    const paneKey = "scenario-segments";
    let pane = map.getPane(paneKey);
    if (!pane) pane = map.createPane(paneKey);
    pane.style.zIndex = "500";

    const customPaneRenderer = L.canvas({
      tolerance: 5,
      pane: paneKey,
    });

    const geosjson = L.geoJSON(prepareSegments(props.segments), {
      // @ts-ignore: missing type and documentation. Issue made: https://github.com/Leaflet/Leaflet/issues/8289
      renderer: customPaneRenderer,
      bubblingMouseEvents: false,
      onEachFeature: handleEachFeature,
      style: {
        color: "#40abff",
      },
    }).addTo(map);

    // cleanup
    return function cleanup() {
      geosjson.remove();
    };
  }, [handleEachFeature, map, props.segments]);

  return null;
});

function prepareSegments(segments: Segment[]) {
  const features = segments.map((segment) => {
    const geometry = wkx.Geometry.parse(segment.lineString_frontendSrid);
    const feature = TurfHelpers.feature(geometry.toGeoJSON(), {
      id: segment.segmentId,
    });
    return feature;
  });

  return TurfHelpers.featureCollection(features);
}
